import styled, { css } from 'styled-components'
import hexToRgba from '../../utils/hexToRgba'

import PressStart2PRegular from './fonts/PressStart2P-Regular.woff2'

const borderColor = (theme) => hexToRgba(theme.colors.content, 0.16)
const barColor = (theme) => hexToRgba(theme.colors.content, 0.06)

export const ButtonToggle = styled.button.attrs({
  className: 'music-player__button-toggle',
})`
  ${({ theme }) => css`
    position: fixed;
    bottom: 40px;
    left: 0;
    width: 42px;
    height: 42px;
    background: ${theme.colors.body};
    border: 1px solid ${borderColor(theme)};
    border-left: none;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    color: ${theme.colors.main};
    z-index: 100;
    transition: filter 0.2s linear;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:focus {
      filter: brightness(0.96);
    }

    > svg {
      width: 24px;
      height: 24px;
    }
  `}
`

const playerModifiers = {
  isOpen: () => css`
    opacity: 1;
    pointer-events: all;
  `,
}

export const Player = styled.div.attrs({
  className: 'music-player',
})`
  @font-face {
    font-family: 'Press Start 2P';
    font-style: normal;
    font-weight: 400;
    src: local(''), url(${PressStart2PRegular}) format('woff2');
  }

  ${({ theme, isOpen }) => css`
    position: fixed;
    bottom: 40px;
    left: 56px;
    width: 320px;
    background: ${theme.colors.body};
    padding: 24px;
    border: 1px solid ${borderColor(theme)};
    border-radius: 4px;
    box-shadow: 1px 2px 8px 0 rgba(0, 0, 0, 0.08);
    z-index: 100;
    box-sizing: border-box;

    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s;
    ${isOpen && playerModifiers.isOpen()}

    @media (max-width: 540px) {
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0;
      box-shadow: none;
      z-index: 1000;

      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  `}
`

export const ButtonClose = styled.button.attrs({
  className: 'music-player__button-close',
})`
  ${({ theme }) => css`
    color: ${theme.colors.content};
    background: none;
    font-size: 0;
    position: absolute;
    top: 0;
    right: 0;
    width: 44px;
    height: 44px;
    transition: opacity 0.2s linear;
    opacity: 0.6;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:focus {
      opacity: 1;
    }

    > svg {
      width: 12px;
      height: 12px;
    }

    @media (max-width: 540px) {
      width: 56px;
      height: 56px;

      > svg {
        width: 16px;
        height: 16px;
      }
    }
  `}
`

export const Info = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 540px) {
    flex-direction: column;
    margin-bottom: 32px;
  }
`

export const Cover = styled.div.attrs({
  className: 'music-player__cover',
})`
  ${({ theme }) => css`
    border-radius: 12px;
    box-shadow: 0 4px 0 1px rgba(0, 0, 0, 0.08),
      inset 0 4px 0 1px rgba(255, 255, 255, 0.2);
    background: ${theme.colors.main};
    width: 80px;
    height: 80px;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    > svg {
      color: ${theme.colors.body};
      width: 60%;
      filter: drop-shadow(0px 4px 0px rgba(255, 255, 255, 0.2));
    }

    @media (max-width: 540px) {
      width: 120px;
      height: 120px;
      margin-right: unset;
      margin-bottom: 16px;

      > svg {
        filter: drop-shadow(0px 8px 0px rgba(255, 255, 255, 0.2));
      }
    }
  `}
`

export const MusicInfo = styled.div`
  flex: 1;

  @media (max-width: 540px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

export const MusicArtist = styled.p.attrs({
  className: 'music-player__artist notranslate',
})`
  ${({ theme }) => css`
    font-family: 'Press Start 2P', cursive;
    font-size: 9px;
    line-height: 20px;
    font-weight: normal;
    color: ${theme.colors.content};
    text-align: left;
  `}
`

export const MusicTitle = styled.p.attrs({
  className: 'music-player__title notranslate',
})`
  ${({ theme }) => css`
    font-family: 'Press Start 2P', cursive;
    font-size: 8px;
    line-height: 20px;
    font-weight: normal;
    color: ${theme.colors.content};
    text-align: left;
  `}
`

export const MusicTracks = styled.p.attrs({
  className: 'music-player__tracks',
})`
  ${({ theme }) => css`
    font-family: 'Press Start 2P', cursive;
    font-size: 8px;
    line-height: 16px;
    font-weight: normal;
    color: ${theme.colors.content};
    opacity: 0.6;
    text-align: left;
  `}
`

export const Config = styled.div`
  display: flex;
  align-items: center;
`

export const ProgressWrapper = styled.div.attrs({
  className: 'music-player__progress',
})`
  width: 100%;
`

export const Progress = styled.div`
  ${({ theme }) => css`
    position: relative;
    height: 6px;
    border-radius: 1px;
    background: ${barColor(theme)};

    @media (max-width: 540px) {
      height: 8px;
      border-radius: 2px;
    }
  `}
`

const progressWidth = ({ progress, isLoading }) => ({
  style: {
    width: isLoading ? '0%' : `${progress}%`,
  },
})

export const ProgressSlider = styled.div.attrs(progressWidth)`
  ${({ theme }) => css`
    position: relative;
    height: 6px;
    border-radius: 1px;
    background: ${theme.colors.main};
    box-shadow: inset 1px 1px 2px 0 rgba(0, 0, 0, 0.2);

    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 100%;
      transform: translate(-50%, -50%);
      display: block;
      width: 10px;
      height: 10px;
      border-radius: 2px;
      box-shadow: -1px 1px 3px 0 rgba(0, 0, 0, 0.16),
        inset 0 2px 0 1px rgba(255, 255, 255, 0.2);
      background-color: ${theme.colors.main};
    }

    @media (max-width: 540px) {
      height: 8px;
      border-radius: 2px;

      &:after {
        width: 12px;
        height: 12px;
      }
    }
  `}
`

export const ProgressRange = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none; /* Override default CSS styles */
  appearance: none;
  width: 100%;
  cursor: pointer;
  opacity: 0;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`

export const VolumeWrapper = styled.div.attrs({
  className: 'music-player__volume',
})`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const ButtonMute = styled.button`
  ${({ theme }) => css`
    color: ${theme.colors.main};
    font-size: 0;
    margin: 0 8px 0 16px;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    transition: opacity 0.2s linear;

    &:hover,
    &:focus {
      opacity: 0.7;
    }

    &:focus:hover {
      opacity: 0.6;
    }
  `}
`

export const Volume = styled.div`
  ${({ theme }) => css`
    position: relative;
    width: 64px;
    height: 6px;
    border-radius: 1px;
    background: ${barColor(theme)};

    @media (max-width: 540px) {
      height: 8px;
      border-radius: 2px;
    }
  `}
`

const volumeWidth = ({ volume, muted }) => ({
  style: {
    width: muted ? '0%' : `${volume}%`,
  },
})

export const VolumeSlider = styled.div.attrs(volumeWidth)`
  ${({ theme }) => css`
    position: relative;
    height: 6px;
    border-radius: 1px;
    background: ${theme.colors.main};
    box-shadow: inset 1px 1px 2px 0 rgba(0, 0, 0, 0.2);

    @media (max-width: 540px) {
      height: 8px;
      border-radius: 2px;
    }
  `}
`

export const VolumeRange = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none; /* Override default CSS styles */
  appearance: none;
  width: 100%;
  cursor: pointer;
  opacity: 0;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`

export const Time = styled.p.attrs({
  className: 'music-player__time',
})`
  ${({ theme }) => css`
    font-family: 'Press Start 2P', cursive;
    font-size: 8px;
    line-height: 16px;
    font-weight: normal;
    color: ${theme.colors.content};
    opacity: 0.6;
    text-align: left;

    margin-top: 8px;
  `}
`

export const Controls = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 540px) {
    margin-top: 32px;
  }
`

export const Action = styled.button`
  ${({ theme }) => css`
    font-size: 0;
    background: none;
    border: none;
    color: ${theme.colors.main};
    transition: opacity 0.2s linear;
    cursor: pointer;

    width: 48px;
    height: 48px;

    &:disabled {
      cursor: initial;
    }

    > svg circle {
      stroke: ${theme.colors.main};
    }

    @media (max-width: 540px) {
      width: 64px;
      height: 64px;
    }
  `}
`

export const NextPrev = styled(Action)`
  transition: 0.2s linear;

  > svg {
    width: 24px;
    filter: drop-shadow(0px 4px 0px rgba(0, 0, 0, 0.08));
    transition: 0.2s linear;
  }

  &:focus {
    filter: brightness(0.9);
  }

  &:hover > svg {
    filter: drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.08));
  }

  &:active {
    transform: scale(0.92);
    > svg {
      filter: drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.08));
    }
  }

  @media (max-width: 540px) {
    > svg {
      width: 32px;
    }
  }
`

export const TogglePlay = styled(Action)`
  ${({ theme }) => css`
    margin: 0 8px;
    border-radius: 8px;
    box-shadow: 0 4px 0 1px rgba(0, 0, 0, 0.08),
      inset 0 4px 0 0 rgba(255, 255, 255, 0.2);
    border: solid 2px ${theme.colors.main};
    background-color: ${theme.colors.main};
    color: ${theme.colors.body};
    transition: 0.2s linear;

    &:focus {
      filter: brightness(0.9);
    }

    &:hover {
      box-shadow: 0 0px 0 0 rgba(0, 0, 0, 0.08),
        inset 0 0px 0 0 rgba(255, 255, 255, 0.2);
    }

    &:active {
      transform: scale(0.96);
      box-shadow: 0 0px 0 0 rgba(0, 0, 0, 0.08),
        inset 0 0px 0 0 rgba(255, 255, 255, 0.2);
    }

    > svg {
      width: 100%;
      height: 100%;
    }
  `}
`

export const LoadingButton = styled(Action)`
  margin: 0 8px;
`
